import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Divider,
  CircularProgress,
} from "@material-ui/core";
import io from "socket.io-client";
import ChatList from "../components/ChatList";
import ChatWindow from "../components/ChatWindow";
import api from "../services/api";
import { toast } from "react-toastify";

/**
 * Tela principal de Pedidos/Chats
 */
export default function Pedidos() {
  /* ========================================================================
   * STATE
   * ===================================================================== */
  const [tab, setTab] = useState(0); // 0=andamento, 1=producao, 2=delivery
  const [chats, setChats] = useState([]); // contém pedidos/chats
  const [filteredChats, setFilteredChats] = useState([]); // lista visível na aba
  const [selecionado, setSelecionado] = useState(null); // item selecionado (pedido/chat)
  const [mensagens, setMensagens] = useState([]); // mensagens do chat selecionado
  const [loading, setLoading] = useState(true);

  // 🔌 Conexão WebSocket estável entre renders
  const socketRef = useRef(null);

  // Para estabilizar handlers e evitar re-registros desnecessários
  const baseSocketUrl = useMemo(
    () => process.env.REACT_APP_API,
    []
  );

  /* ========================================================================
   * SOCKET.IO: Conexão e listeners
   * ===================================================================== */
  useEffect(() => {
    const socket = io(baseSocketUrl, { transports: ["websocket"] });
    socketRef.current = socket;

    // ---------------- PEDIDOS ----------------
    socket.on("novoPedido", (pedido) => {
      setChats((prev) => {
        const existe = prev.some(
          (p) => p.id === pedido.id || p.telefone === pedido.telefone
        );
        const next = existe
          ? prev.map((p) =>
              p.id === pedido.id || p.telefone === pedido.telefone
                ? { ...p, ...pedido }
                : p
            )
          : [{ ...pedido }, ...prev];
        return next;
      });

      // 🔥 Atualiza lista filtrada imediatamente
      setFilteredChats((prev) => {
        const pertence =
          pedido.status ===
          (tab === 0 ? "andamento" : tab === 1 ? "producao" : "delivery");
        if (pertence) {
          const existe = prev.some(
            (p) => p.id === pedido.id || p.telefone === pedido.telefone
          );
          return existe
            ? prev.map((p) => (p.id === pedido.id ? pedido : p))
            : [pedido, ...prev];
        }
        return prev;
      });

      toast.info(`Novo pedido de ${pedido.nome || "cliente"} recebido!`);
    });

    socket.on("pedidoNovo", (pedido) => {
      setChats((prev) => [pedido, ...prev]);
      toast.success(`Novo pedido criado: ${pedido.nome || pedido.telefone}`);
    });

    socket.on("pedidoAtualizado", (pedido) => {
      setChats((prev) =>
        prev.map((p) => (p.id === pedido.id ? { ...p, ...pedido } : p))
      );

      if (selecionado?.id === pedido.id) {
        setSelecionado((s) => (s ? { ...s, ...pedido } : s));
      }
    });

    socket.on("pedidoRemovido", (id) => {
      setChats((prev) => prev.filter((p) => p.id !== id));
      if (selecionado?.id === id) {
        setSelecionado(null);
        setMensagens([]);
      }
    });

    // ---------------- CHATS ----------------
    socket.on("novoChat", (chat) => {
      setChats((prev) => {
        const exists = prev.some((c) => c.id === chat.id);
        return exists ? prev : [chat, ...prev];
      });
    });

    socket.on("chatAtualizado", (chat) => {
      setChats((prev) =>
        prev.map((c) => (c.id === chat.id ? { ...c, ...chat } : c))
      );

      if (selecionado?.id === chat.id)
        setSelecionado((s) => (s ? { ...s, ...chat } : s));
    });

    socket.on("chatFinalizado", (payload) => {
      const id =
        typeof payload === "object" && payload
          ? payload.chatId || payload.id
          : payload;
      if (!id) return;

      setChats((prev) =>
        prev.map((c) => (c.id === id ? { ...c, status: "finalizado" } : c))
      );
      if (selecionado?.id === id) {
        setSelecionado((s) => (s ? { ...s, status: "finalizado" } : s));
      }
    });

    // ---------------- MENSAGENS ----------------
    socket.on("mensagem", (msg) => {
      const cid = msg.chatId || msg.ChatId;
      if (!cid) return;

      // ✅ Evita processar mensagens duplicadas entre "mensagem" e "novaMensagem"
      if (msg.__handled) return;
      msg.__handled = true;

      if (selecionado && cid === selecionado.id) {
        setMensagens((prev) => {
          if (!msg.texto && !msg.Body && !msg.midiaUrl) return prev;

          // Evita duplicar (mesmo id ou mesmo texto e hora muito próxima)
          const jaExiste = prev.some(
            (m) =>
              (m.id && msg.id && m.id === msg.id) ||
              (m.texto === msg.texto &&
                Math.abs(
                  new Date(m.dataHora || m.createdAt).getTime() -
                    new Date(msg.dataHora || msg.createdAt).getTime()
                ) < 800)
          );
          if (jaExiste) return prev;

          return [...prev, msg];
        });
      }

      setChats((prev) =>
        prev.map((c) =>
          c.id === cid
            ? {
                ...c,
                ultimoTexto:
                  msg.texto ||
                  msg.Body ||
                  (msg.midiaUrl ? `[${msg.tipo || "mídia"}]` : c.ultimoTexto),
                ultimaMensagemAt:
                  msg.dataHora || msg.createdAt || new Date().toISOString(),
              }
            : c
        )
      );
    });

    socket.on("novaMensagem", (data) => {
      const cid = data?.chatId;
      const m = data?.mensagem;
      if (!m || !cid) return;

      // ✅ Evita duplicação entre "mensagem" e "novaMensagem"
      if (m.__handled) return;
      m.__handled = true;

      if (selecionado?.id === cid) {
        setMensagens((prev) => {
          if (!m.texto && !m.Body && !m.midiaUrl) return prev;

          const jaExiste = prev.some(
            (x) =>
              (x.id && m.id && x.id === m.id) ||
              (x.texto === m.texto &&
                Math.abs(
                  new Date(x.dataHora || x.createdAt).getTime() -
                    new Date(m.dataHora || m.createdAt).getTime()
                ) < 800)
          );
          if (jaExiste) return prev;

          return [...prev, m];
        });
      }

      setChats((prev) =>
        prev.map((c) =>
          c.id === cid
            ? {
                ...c,
                ultimoTexto:
                  m.texto ||
                  m.Body ||
                  (m.midiaUrl ? `[${m.tipo || "mídia"}]` : c.ultimoTexto),
                ultimaMensagemAt:
                  m.dataHora || m.createdAt || new Date().toISOString(),
              }
            : c
        )
      );
    });

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseSocketUrl, selecionado?.id, tab]);

  /* ========================================================================
   * Sincroniza filteredChats sempre que chats mudar
   * ===================================================================== */
  const statusAtual = useMemo(
    () => (tab === 0 ? "andamento" : tab === 1 ? "producao" : "delivery"),
    [tab]
  );

  useEffect(() => {
    const filtrados = chats.filter((c) => c.status === statusAtual);
    setFilteredChats(filtrados);

    if (selecionado && !filtrados.some((c) => c.id === selecionado.id)) {
      setSelecionado(null);
      setMensagens([]);
    }
  }, [tab, chats, statusAtual, selecionado?.id]);

  /* ========================================================================
   * CARREGAR LISTA INICIAL
   * ===================================================================== */
  useEffect(() => {
    async function carregarPedidos() {
      try {
        const { data } = await api.get("/pedidos");
        setChats(data || []);
      } catch (error) {
        console.error("❌ Erro ao carregar pedidos:", error);
        toast.error("Erro ao carregar pedidos.");
      } finally {
        setLoading(false);
      }
    }
    carregarPedidos();
  }, []);

  /* ========================================================================
   * SELECIONAR CHAT/PEDIDO
   * ===================================================================== */
  const handleSelectChat = useCallback(
    async (item) => {
      if (!item) return;
      setSelecionado(item);

      // 🔹 Garante que há um chatId antes de tentar buscar
      const chatId = item.chatId || item.id;

      if (!chatId) {
        console.warn("⚠️ Pedido ainda sem chatId definido:", item);
        toast.info(
          "O pedido ainda está sendo sincronizado, tente novamente em 1 segundo."
        );
        return;
      }

      try {
        const { data } = await api.get(`/chats/${chatId}/mensagens`);
        setMensagens(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("❌ Erro ao carregar mensagens do chat:", err);
        toast.error("Erro ao carregar mensagens do chat.");
        setMensagens([]);
      }
    },
    [setSelecionado, setMensagens]
  );

  /* ========================================================================
   * Recarrega mensagens automaticamente se o chatId surgir depois
   * ===================================================================== */
  useEffect(() => {
    if (
      selecionado &&
      !mensagens.length &&
      (selecionado.chatId || selecionado.id)
    ) {
      (async () => {
        try {
          const chatId = selecionado.chatId || selecionado.id;
          const { data } = await api.get(`/chats/${chatId}/mensagens`);
          if (Array.isArray(data)) setMensagens(data);
        } catch (err) {
          console.error("Erro ao recarregar mensagens:", err);
        }
      })();
    }
  }, [selecionado?.chatId]);

  /* ========================================================================
   * ENVIAR MENSAGEM
   * ===================================================================== */
  const handleSend = useCallback(
    (payload) => {
      if (!selecionado || !socketRef.current) {
        console.warn("⚠️ Nenhum chat selecionado ou socket não disponível");
        return;
      }

      const body =
        typeof payload === "string"
          ? payload.trim()
          : payload?.Body?.trim();
      const numero =
        (typeof payload === "object" && payload?.Phone) ||
        selecionado.telefone ||
        selecionado.numero ||
        selecionado.phone;

      if (!body || !numero) return;

      const otimista = {
        texto: body,
        remetente: "atendente",
        direcao: "out",
        tipo: "text",
        midiaUrl: null,
        dataHora: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setMensagens((prev) => [...prev, otimista]);

      socketRef.current.emit("mensagem", {
        chatId: selecionado.id,
        Phone: numero,
        Body: body,
        remetente: "atendente",
      });
    },
    [selecionado]
  );

  /* ========================================================================
   * RENDER
   * ===================================================================== */
  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Tabs
        value={tab}
        onChange={(_, newValue) => setTab(newValue)}
        indicatorColor="primary"
        textColor="primary"
        centered
      >
        <Tab label="🧾 Pedidos" />
        <Tab label="🏭 Produção" />
        <Tab label="🚚 Delivery" />
      </Tabs>
      <Divider />

      {loading ? (
        <Box
          flexGrow={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box display="flex" flexGrow={1} overflow="hidden">
          <ChatList
            key={`${tab}-${filteredChats.length}`}
            chats={filteredChats}
            selectedId={selecionado?.id}
            onSelect={handleSelectChat}
          />

          <Box flexGrow={1} minWidth={0}>
            {selecionado ? (
              <ChatWindow
                chat={selecionado}
                mensagens={mensagens}
                onSend={handleSend}
              />
            ) : (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100%"
              >
                <Typography variant="h6" color="textSecondary">
                  Selecione um pedido para visualizar
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
